import { formatProjectTypeLabel } from '@kaiizen/kai-presenters';
import { STANDARD_PROJECT_TYPES } from '@kaiizen/kai-pro-api';

export type ProjectTypeOption = {
  value: string;
  label: string;
};

export type ProjectTypeSettings = {
  displayName?: string;
  description?: string;
  isEnabled?: boolean;
};

export type ProjectTypesRecord = Record<string, ProjectTypeSettings>;

export type BaseProjectTypeDefinition = {
  key: string;
  displayName: string;
  description: string;
};

export const ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS: readonly BaseProjectTypeDefinition[] = [
  {
    key: 'acquisition',
    displayName: STANDARD_PROJECT_TYPES.acquisition,
    description: "A property you're buying. Scope and document work needed to bring it up to standard before or right after purchase.",
  },
  {
    key: 'disposition',
    displayName: STANDARD_PROJECT_TYPES.disposition,
    description: "A property you're selling. Track repairs and prep work to get it market-ready and maximize sale value.",
  },
  {
    key: 'turn',
    displayName: STANDARD_PROJECT_TYPES.turn,
    description: 'A property between tenants. Handle the cleaning, repairs, and refresh needed to make it rent-ready for the next resident.',
  },
  {
    key: 'renovation',
    displayName: STANDARD_PROJECT_TYPES.renovation,
    description: "Any other project. Use this for general improvements, repairs, or upgrades that don't fit the other types.",
  },
  {
    key: 'training',
    displayName: STANDARD_PROJECT_TYPES.training,
    description: "A practice project for learning Kai. Won't affect budgets, reporting, or live data.",
  },
];

const DEFAULT_PROJECT_TYPE_DISPLAY_NAMES: Record<string, string | undefined> = Object.fromEntries(
  ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS.map(({ key, displayName }) => [
    key,
    displayName,
  ]),
);

const PREVIOUS_DEFAULT_PROJECT_TYPE_DISPLAY_NAMES: Record<string, string> = {
  acquisition: 'Acquisition Project',
  disposition: 'Disposition Project',
  turn: 'Turn Project',
  training: 'Training Project',
};

export const getDefaultOrgSettingsProjectTypes = (): ProjectTypesRecord =>
  Object.fromEntries(
    ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS.map(({ key }) => [key, {}]),
  );

const LEGACY_PROTOTYPE_PROJECT_TYPES: ProjectTypesRecord = {
  demoSite: {
    displayName: 'Demo Site',
    description: 'Demo site',
  },
  renovation: {
    displayName: 'Renovation',
    description: 'Routine renovation between tenants',
  },
  capitalExpense: {
    displayName: 'Capital Expense',
    description: 'Large-scale improvements that increase the value or extend the useful life of a property.',
  },
};

const isLegacyPrototypeProjectTypeEntry = ([
  value,
  projectType,
]: [string, ProjectTypeSettings]): boolean => {
  const legacyConfig = LEGACY_PROTOTYPE_PROJECT_TYPES[value];

  return (
    !!legacyConfig
    && projectType.displayName?.trim() === legacyConfig.displayName
    && (projectType.description?.trim() || '')
      === (legacyConfig.description?.trim() || '')
  );
};

const isLegacyPrototypeProjectTypes = (
  projectTypes: ProjectTypesRecord | undefined,
): boolean => {
  if (!projectTypes) return false;

  const projectTypeEntries = Object.entries(projectTypes);

  return (
    projectTypeEntries.length > 0
    && projectTypeEntries.every(isLegacyPrototypeProjectTypeEntry)
  );
};

const isDefaultProjectTypeDisplayName = (
  projectTypeKey: string,
  displayName: string | undefined,
) =>
  !displayName
  || displayName === DEFAULT_PROJECT_TYPE_DISPLAY_NAMES[projectTypeKey]
  || displayName === PREVIOUS_DEFAULT_PROJECT_TYPE_DISPLAY_NAMES[projectTypeKey];

const normalizeProjectTypeSettings = (
  projectTypeKey: string,
  projectType: ProjectTypeSettings | undefined,
): ProjectTypeSettings => {
  const displayName = projectType?.displayName?.trim();
  const description = projectType?.description?.trim();

  return {
    ...(!isDefaultProjectTypeDisplayName(projectTypeKey, displayName) && {
      displayName,
    }),
    ...(description && { description }),
    ...(projectType?.isEnabled === false && { isEnabled: false }),
  };
};

export const getOrgSettingsProjectTypesOrDefaults = (
  projectTypes: ProjectTypesRecord | undefined,
): ProjectTypesRecord => {
  if (
    !projectTypes
    || Object.keys(projectTypes).length === 0
    || isLegacyPrototypeProjectTypes(projectTypes)
  ) {
    return getDefaultOrgSettingsProjectTypes();
  }

  return Object.fromEntries(
    ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS.map(({ key }) => [
      key,
      normalizeProjectTypeSettings(key, projectTypes[key]),
    ]),
  );
};

export const getProjectTypeLabelEntries = (
  projectTypes: ProjectTypesRecord | undefined,
): Record<string, string> =>
  Object.fromEntries(
    ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS.map(({ key, displayName }) => {
      const projectType = getOrgSettingsProjectTypesOrDefaults(projectTypes)[key];
      return [key, projectType.displayName?.trim() || displayName];
    }),
  );

export const getOrgSettingsProjectTypeOptions = (
  projectTypes: ProjectTypesRecord | undefined,
  currentProjectType?: string | null,
): ProjectTypeOption[] => {
  const normalizedProjectTypes = getOrgSettingsProjectTypesOrDefaults(projectTypes);
  const projectTypeLabels = getProjectTypeLabelEntries(normalizedProjectTypes);

  const enabledOptions = ORG_SETTINGS_PROJECT_TYPE_DEFINITIONS
    .filter(({ key }) => normalizedProjectTypes[key]?.isEnabled !== false)
    .map(({ key }) => ({
      value: key,
      label: projectTypeLabels[key] || formatProjectTypeLabel(key),
    }));

  if (
    !currentProjectType
    || enabledOptions.some(option => option.value === currentProjectType)
  ) {
    return enabledOptions;
  }

  return [
    ...enabledOptions,
    {
      value: currentProjectType,
      label: projectTypeLabels[currentProjectType]
        || formatProjectTypeLabel(currentProjectType),
    },
  ];
};
